// ══════════════════════════════════════════════════════════════
// COMPASS – Language Service
// ══════════════════════════════════════════════════════════════
import { DOCUMENT, registerLocaleData } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom } from 'rxjs';
import { Logger } from '../services/logger.service';
import { activeLocale, requestLanguage } from './active-locale';
import {
  DEFAULT_LANGUAGE_CONFIG,
  LanguageConfig,
  SUPPORTED_LANGUAGES,
  getLanguageConfig,
  isSupportedLanguage,
  resolveSupportedLanguage,
} from './languages.config';

/** localStorage key holding the language the user explicitly selected. */
export const LANGUAGE_STORAGE_KEY = 'compass_language';
/** localStorage flag: the user picked a language that the backend has not stored yet. */
export const LANGUAGE_UNSYNCED_KEY = 'compass_language_unsynced';

/** Who changed the language: app start-up, the user (selector) or the user's backend preference. */
export type LanguageChangeSource = 'init' | 'user' | 'backend';

export interface LanguageChange {
  language: LanguageConfig;
  source: LanguageChangeSource;
}

/**
 * Owns the active UI language.
 *
 * Startup order: saved preference → closest browser language → default (English).
 * Only an explicit selection is saved, so browser detection keeps applying until
 * the user picks a language, and a saved choice always wins over the browser locale.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  /** Optional: only provided where the Material datepicker is configured (app.config). */
  private readonly dateAdapter = inject(DateAdapter, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly logger = inject(Logger);
  private readonly registeredLocales = new Set<string>();

  private readonly currentLanguage = signal<LanguageConfig>(DEFAULT_LANGUAGE_CONFIG);
  private readonly changesSubject = new Subject<LanguageChange>();

  /** Active language. */
  readonly current = this.currentLanguage.asReadonly();
  /** Languages offered by the selector. */
  readonly languages = SUPPORTED_LANGUAGES;
  /** Every applied language change, with its source (used to sync the backend preference and refetch data). */
  readonly changes$ = this.changesSubject.asObservable();

  /** Apply the initial language. Called once by an app initializer. */
  async init(): Promise<void> {
    await this.apply(this.resolveInitialLanguage());
    this.changesSubject.next({ language: this.currentLanguage(), source: 'init' });
  }

  /**
   * Switch language and save it locally. `user` (default) is a choice made in the selector:
   * it is flagged as not yet stored on the backend until markPreferenceSynced() is called.
   * `backend` applies the preference stored on the user's account.
   */
  async setLanguage(code: string, source: Exclude<LanguageChangeSource, 'init'> = 'user'): Promise<void> {
    const config = getLanguageConfig(code);
    if (!config) {
      this.logger.warn(`Unsupported language: ${code}`);
      return;
    }

    await this.apply(config);
    if (this.currentLanguage().code !== config.code) return; // fell back to the default

    this.savePreference(config.code);
    if (source === 'user') {
      this.writeStorage(LANGUAGE_UNSYNCED_KEY, '1');
    }
    this.changesSubject.next({ language: config, source });
  }

  /** True when the user picked a language that the backend has not stored yet. */
  hasUnsyncedPreference(): boolean {
    return this.readStorage(LANGUAGE_UNSYNCED_KEY) === '1';
  }

  /** True when a language was chosen on this device (selector or account), not just detected from the browser. */
  hasSavedPreference(): boolean {
    return isSupportedLanguage(this.readPreference());
  }

  /**
   * The backend now stores the current language. It is also saved on this device, so later logins
   * here follow the account's language instead of the browser's.
   */
  markPreferenceSynced(): void {
    this.savePreference(this.currentLanguage().code);
    this.removeStorage(LANGUAGE_UNSYNCED_KEY);
  }

  /** Saved preference → closest browser language → default. */
  resolveInitialLanguage(): LanguageConfig {
    return getLanguageConfig(this.readPreference())
      ?? this.detectBrowserLanguage()
      ?? DEFAULT_LANGUAGE_CONFIG;
  }

  private detectBrowserLanguage(): LanguageConfig | undefined {
    const navigator = this.document.defaultView?.navigator;
    const locales = navigator?.languages?.length ? navigator.languages : [navigator?.language];
    for (const locale of locales) {
      const match = resolveSupportedLanguage(locale);
      if (match) return match;
    }
    return undefined;
  }

  private async apply(config: LanguageConfig): Promise<void> {
    // API calls made while translations load must already ask for the new language.
    requestLanguage.set(config.code);
    try {
      // Locale data must be registered before translate.use() re-renders date pipes.
      await this.registerLocale(config);
      await firstValueFrom(this.translate.use(config.code));
    } catch (error) {
      this.logger.error(`Failed to load language ${config.code}:`, error);
      if (config.code !== DEFAULT_LANGUAGE_CONFIG.code) {
        return this.apply(DEFAULT_LANGUAGE_CONFIG);
      }
    }

    this.document.documentElement.lang = config.code;
    this.dateAdapter?.setLocale(config.dateLocale);
    activeLocale.set(config.code);
    this.currentLanguage.set(config);
  }

  private async registerLocale(config: LanguageConfig): Promise<void> {
    if (!config.loadLocaleData || this.registeredLocales.has(config.code)) return;
    registerLocaleData(await config.loadLocaleData());
    this.registeredLocales.add(config.code);
  }

  private readPreference(): string | null {
    return this.readStorage(LANGUAGE_STORAGE_KEY);
  }

  private savePreference(code: string): void {
    this.writeStorage(LANGUAGE_STORAGE_KEY, code);
  }

  // Storage can be unavailable (private mode / blocked): the choice then lasts for this session only.
  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }

  private removeStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}
